/**
 * The small, shared pieces of ИИ-подбор: the ✦ mark, the quota line, and the
 * cards for the states where there is no proposal to show.
 *
 * Every one of these states keeps the MANUAL path on screen. The AI is an
 * addition to the category picker, never a replacement for it, so none of these
 * cards is allowed to be a dead end — that is the whole reason each has two
 * actions rather than an «ОК».
 */
import type { ReactNode } from 'react';
import { useLocale } from '../i18n/locale-context';
import { pluralKeyIndex } from '../lib/format';
import { quotaLeft, type AiQuota } from '../lib/ai-search-client';

const PLURAL_SUFFIX = ['one', 'few', 'many'] as const;

/** The mark that says "a model filled this in". Decoration — every place that
 *  uses it also carries a readable sentence, so it is hidden from the a11y
 *  tree rather than announced as a character name. */
export function AiSparkle({ className = '' }: { className?: string }) {
  return (
    <span aria-hidden="true" className={`text-brand ${className}`}>
      ✦
    </span>
  );
}

/**
 * How many proposals are left — shown BEFORE the button is pressed.
 *
 * After is too late: someone who spends their second and last attempt on a
 * half-written sentence was never told it was their last. The two shapes match
 * the two mockups — a bare counter next to the field in onboarding, a full
 * sentence with the Pro allowance on the saved-search form.
 */
export function AiQuotaLine({
  quota,
  variant,
  className = '',
}: {
  quota: AiQuota | null;
  variant: 'counter' | 'sentence';
  className?: string;
}) {
  const { t } = useLocale();
  if (!quota) return null;
  const left = quotaLeft(quota);
  if (variant === 'counter') {
    return (
      <span className={`text-[12.5px] text-ink-muted ${className}`}>
        {t('aiOnboarding.quota.counter', { left, limit: quota.limit })}
      </span>
    );
  }
  return (
    <p className={`text-[12.5px] text-ink-muted ${className}`}>
      {t('aiOnboarding.quota.left', {
        n: left,
        word: t(`aiOnboarding.quota.word.${PLURAL_SUFFIX[pluralKeyIndex(left)]}`),
      })}
    </p>
  );
}

/** One of the "no proposal" cards. `tone` picks the frame, never the wording. */
function AiCard({
  tone = 'plain',
  title,
  desc,
  children,
}: {
  tone?: 'plain' | 'warn';
  title: ReactNode;
  desc: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        tone === 'warn'
          ? 'border-status-closing-dot/40 bg-status-closing-bg'
          : 'border-edge bg-bone-surface'
      }`}
      role="group"
    >
      <h3
        className={`mb-1 text-[15px] font-bold ${
          tone === 'warn' ? 'text-status-closing-text' : 'text-ink'
        }`}
      >
        {title}
      </h3>
      <p className="mb-3 text-[13px] leading-[1.5] text-ink-soft">{desc}</p>
      {children}
    </div>
  );
}

/** Two side-by-side actions — the layout every card in mockup 5 uses. */
function AiActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2 [&>*]:min-w-[132px] [&>*]:flex-1">{children}</div>;
}

const PRIMARY =
  'inline-flex h-11 items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60';
const SECONDARY =
  'inline-flex h-11 items-center justify-center rounded-md border border-edge bg-bone-surface px-4 text-[13px] font-bold text-brand transition-colors hover:border-brand disabled:pointer-events-none disabled:opacity-60';

export { PRIMARY as aiPrimaryClass, SECONDARY as aiSecondaryClass };

/**
 * (а) The model could not tell what the person supplies.
 *
 * NOT a red error: nothing is broken and nothing was charged. The user wrote
 * something too short or too abstract, so the fix is a nudge with an example
 * plus the manual door — and the field keeps what they typed, because asking
 * someone to retype the sentence you just rejected is how they leave.
 */
export function AiNotUnderstoodCard({
  onRefine,
  onManual,
}: {
  onRefine: () => void;
  onManual: () => void;
}) {
  const { t } = useLocale();
  return (
    <AiCard
      title={t('aiOnboarding.notUnderstood.title')}
      desc={t('aiOnboarding.notUnderstood.desc')}
    >
      <AiActions>
        <button type="button" onClick={onRefine} className={SECONDARY}>
          {t('aiOnboarding.notUnderstood.refine')}
        </button>
        <button type="button" onClick={onManual} className={PRIMARY}>
          {t('aiOnboarding.notUnderstood.manual')}
        </button>
      </AiActions>
    </AiCard>
  );
}

/**
 * (г) No proposals left.
 *
 * The wording depends on the SCOPE, not on the plan name: a free account has
 * spent two for good, a paying one has spent this month's fifteen and gets more
 * in a few days. Telling a subscriber to «открыть Pro» when they already have
 * it is the kind of copy that produces support tickets.
 */
export function AiQuotaOutCard({
  quota,
  onManual,
  onPro,
}: {
  quota: AiQuota | null;
  onManual: () => void;
  onPro: () => void;
}) {
  const { t } = useLocale();
  const monthly = quota?.scope === 'month';
  return (
    <AiCard
      title={t('aiOnboarding.quotaOut.title')}
      desc={monthly ? t('aiOnboarding.quotaOut.descMonth') : t('aiOnboarding.quotaOut.desc')}
    >
      <AiActions>
        <button type="button" onClick={onManual} className={SECONDARY}>
          {t('aiOnboarding.quotaOut.manual')}
        </button>
        {!monthly && (
          <button type="button" onClick={onPro} className={PRIMARY}>
            {t('aiOnboarding.quotaOut.pro')}
          </button>
        )}
      </AiActions>
    </AiCard>
  );
}

/**
 * (д) The model timed out, errored, or the request never landed.
 *
 * The attempt is NOT counted — the server says so (`consumed: false`) and the
 * copy says so too, because "try again" next to a counter that just ticked down
 * is a reason to distrust the counter. «Повторить» re-sends the same text; the
 * manual picker is the primary action because it always works.
 */
export function AiErrorCard({ onManual, onRetry }: { onManual: () => void; onRetry: () => void }) {
  const { t } = useLocale();
  return (
    <AiCard title={t('aiOnboarding.error.title')} desc={t('aiOnboarding.error.desc')}>
      <AiActions>
        <button type="button" onClick={onManual} className={PRIMARY}>
          {t('aiOnboarding.error.manual')}
        </button>
        <button type="button" onClick={onRetry} className={SECONDARY}>
          {t('aiOnboarding.error.retry')}
        </button>
      </AiActions>
    </AiCard>
  );
}
